import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connectorOrigin, connectorRequest } from './connectorApi';
import { useConnectorAccount } from './useConnectorAccount';

export const supportLink = 'mailto:hassan@jobseeqr.co?subject=Talent%20Search%20connector%20activation';

function SetupInstructions({ assistant, onClose, t }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const endpoint = `${connectorOrigin}/mcp`;
  async function copy() {
    try { await navigator.clipboard.writeText(endpoint); setCopied(true); }
    catch { setCopyError(true); }
  }
  return <section className="profile-panel connector-setup" aria-label={t('connector.setupTitle', { assistant })}>
    <div className="profile-panel-header">
      <h3>{t('connector.setupTitle', { assistant })}</h3>
      <button type="button" className="profile-inline-action" onClick={onClose}>{t('connector.close')}</button>
    </div>
    <ol className="connector-steps">
      <li>{t('connector.setupOne', { assistant })}</li>
      <li>{t('connector.setupTwo')}</li>
      <li>{t('connector.setupThree')}</li>
    </ol>
    <label className="profile-preferences-label" htmlFor="connector-endpoint">{t('connector.serverAddress')}</label>
    <div className="connector-copy-row">
      <input id="connector-endpoint" className="profile-input" readOnly value={endpoint} dir="ltr" onFocus={e => e.target.select()} />
      <button type="button" className="profile-btn-secondary" onClick={copy}>{t(copied ? 'connector.copied' : 'connector.copy')}</button>
    </div>
    {copyError && <p role="status" className="profile-muted-copy">{t('connector.copyManually')}</p>}
    <p className="profile-muted-copy">{t('connector.setupNote')}</p>
    <a className="profile-btn-primary" href={assistant === 'ChatGPT' ? 'https://chatgpt.com/' : 'https://claude.ai/'} target="_blank" rel="noopener noreferrer">
      {t('connector.openAssistant', { assistant })} <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
    </a>
  </section>;
}

export default function TalentConnectorSettings({ currentUser, section = 'connections' }) {
  const { t, i18n } = useTranslation('profile');
  const { data, loading, error, refresh } = useConnectorAccount(currentUser);
  const [params] = useSearchParams();
  const [setup, setSetup] = useState(() => ({ chatgpt: 'ChatGPT', claude: 'Claude' }[params.get('assistant')] || ''));
  const [confirmId, setConfirmId] = useState('');
  const [busy, setBusy] = useState('');
  const [actionError, setActionError] = useState(false);
  const money = cents => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
  const date = seconds => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(seconds * 1000));
  const balance = data?.balance;
  const ready = data?.activation === 'active';

  async function disconnect(id) {
    setBusy(id); setActionError(false);
    try {
      await connectorRequest(currentUser, 'disconnect', { body: { connection_id: id } });
      setConfirmId(''); refresh();
    } catch { setActionError(true); }
    finally { setBusy(''); }
  }

  return <div className="profile-section-stack">
    <div className="settings-section-heading">
      <div><h2>{t(section === 'usage' ? 'connector.usageTitle' : 'connector.title')}</h2>
        <p>{t(section === 'usage' ? 'connector.usageDescription' : 'connector.description')}</p></div>
      {connectorOrigin && <button type="button" className="profile-btn-secondary" onClick={refresh} disabled={loading}>
        <i className={`fa-solid fa-rotate-right ${loading ? 'fa-spin' : ''}`} aria-hidden="true" /> {t('connector.refresh')}
      </button>}
    </div>
    {loading && <div className="profile-panel settings-loading" role="status">{t('connector.loading')}</div>}
    {error && <div className="settings-notice" role={error === 'not_configured' ? 'status' : 'alert'}>
      <i className="fa-solid fa-circle-info" aria-hidden="true" />
      <div><strong>{t(`connector.errors.${error}`, t('connector.errors.unavailable'))}</strong>
        <p>{t(error === 'not_configured' ? 'connector.notConfiguredHint' : 'connector.retryHint')}</p></div>
    </div>}
    {data && !ready && <div className="settings-notice" role="status">
      <i className="fa-regular fa-clock" aria-hidden="true" />
      <div><strong>{t(data.activation === 'paused' ? 'connector.paused' : 'connector.pending')}</strong>
        <p>{t('connector.manualActivation')}</p></div>
      <a href={supportLink} className="profile-btn-secondary">{t('connector.contactTeam')}</a>
    </div>}

    {section === 'connections' && <>
      <div className="connector-apps">
        {['ChatGPT', 'Claude'].map(assistant => <section className="profile-panel connector-app" key={assistant}>
          <div className="connector-app-heading"><span className={`connector-app-mark ${assistant.toLowerCase()}`} aria-hidden="true"><img src={`${process.env.PUBLIC_URL}/${assistant === 'ChatGPT' ? 'openai' : 'claude'}-logo.svg`} alt="" /></span>
            <div><h3>{assistant}</h3><span>{t('connector.talentSearch')}</span></div></div>
          <p>{t('connector.assistantDescription', { assistant })}</p>
          <button type="button" className="profile-btn-secondary" disabled={!ready || loading} onClick={() => setSetup(assistant)}>
            {t('connector.setup', { assistant })} <i className="fa-solid fa-arrow-right" aria-hidden="true" />
          </button>
        </section>)}
      </div>
      {setup && ready && <SetupInstructions assistant={setup} onClose={() => setSetup('')} t={t} />}
      <section className="profile-panel">
        <div className="profile-panel-header"><div><h3>{t('connector.connectedAccounts')}</h3><p>{t('connector.credentialsDescription')}</p></div></div>
        {actionError && <p className="settings-error" role="alert">{t('connector.disconnectError')}</p>}
        {!loading && !error && !data?.connections?.length && <div className="settings-empty"><i className="fa-solid fa-link" aria-hidden="true" /><p>{t('connector.noConnections')}</p></div>}
        {(data?.connections || []).map(connection => <div className="connector-connection" key={connection.connection_id}>
          <span className="connector-connection-icon" aria-hidden="true"><i className="fa-solid fa-key" /></span>
          <div><strong>{connection.client_name}</strong><p>{t('connector.connectedOn', { date: date(connection.connected_at) })}</p>
            <small>{t('connector.expiresOn', { date: date(connection.expires_at) })}</small></div>
          {confirmId === connection.connection_id ? <div className="connector-revoke-confirm">
            <span>{t('connector.confirmDisconnect')}</span>
            <button type="button" className="profile-btn-secondary" disabled={Boolean(busy)} onClick={() => setConfirmId('')}>{t('cancel')}</button>
            <button type="button" className="profile-btn-danger" disabled={Boolean(busy)} onClick={() => disconnect(connection.connection_id)}>{t(busy ? 'connector.disconnecting' : 'connector.disconnect')}</button>
          </div> : <button type="button" className="profile-btn-secondary" onClick={() => setConfirmId(connection.connection_id)}>{t('connector.disconnect')}</button>}
        </div>)}
      </section>
      <p className="settings-footnote connector-credentials-footer">
        <span className="connector-credentials-note"><i className="fa-solid fa-lock" aria-hidden="true" /><span>{t('connector.credentialsNote')}</span></span>
        <Link to="/profile?tab=usage">{t('connector.viewUsage')}</Link>
      </p>
    </>}

    {section === 'usage' && <>
      <div className="settings-notice">
        <i className="fa-regular fa-credit-card" aria-hidden="true" />
        <div><strong>{t('connector.workspaceBillingTitle')}</strong><p>{t('connector.workspaceBillingDescription')}</p></div>
        <Link to="/profile?tab=billing" className="profile-btn-secondary">{t('connector.viewWorkspaceBilling')}</Link>
      </div>
      {balance?.provisioned && <div className="connector-stats">
        <div className="profile-panel"><span>{t('connector.freeProfiles')}</span><strong>{balance.free_profiles_remaining}</strong><small>{t('connector.freeProfilesHint')}</small></div>
        <div className="profile-panel"><span>{t('connector.freeContacts')}</span><strong>{balance.free_contacts_remaining}</strong><small>{t('connector.freeContactsHint')}</small></div>
        <div className="profile-panel"><span>{t(balance.mode === 'prepaid' ? 'connector.balance' : 'connector.amountDue')}</span><strong>{money(balance.mode === 'prepaid' ? balance.prepaid_cents : balance.amount_due_cents)}</strong><small>{t('connector.reserved', { amount: money(balance.reserved_cents) })}</small></div>
      </div>}
      <section className="profile-panel connector-payment">
        <div className="connector-app-heading"><span className="connector-payment-icon" aria-hidden="true"><i className="fa-solid fa-wallet" /></span><div><h3>{t('connector.paymentsTitle')}</h3><span>{t('connector.manualPayment')}</span></div></div>
        <p>{t('connector.whishDescription')}</p>
        <div className="connector-price-row"><span>{t('connector.searchPrice')}</span><span>{t('connector.contactPrice')}</span><span>{t('connector.opensFree')}</span></div>
        <Link to="/connectors/talent/recharge" className="profile-btn-secondary">{t(balance?.mode === 'prepaid' ? 'connector.addPrepaidFunds' : balance?.mode === 'postpaid' && balance.amount_due_cents > 0 ? 'connector.payAmountDue' : balance?.mode === 'trial' ? 'connector.enablePaidUsage' : 'connector.paymentHelp')} <i className="fa-solid fa-arrow-right" aria-hidden="true" /></Link>
      </section>
      <section className="profile-panel connector-history">
        <div className="profile-panel-header"><div><h3>{t('connector.recentActivity')}</h3><p>{t('connector.sharedUsage')}</p></div></div>
        {!loading && !error && !data?.operations?.length && <div className="settings-empty"><i className="fa-regular fa-file-lines" aria-hidden="true" /><p>{t('connector.noUsage')}</p></div>}
        {Boolean(data?.operations?.length) && <div className="connector-table-wrap"><table><thead><tr>
          <th>{t('connector.activity')}</th><th>{t('connector.date')}</th><th>{t('connector.status')}</th><th>{t('connector.charge')}</th>
        </tr></thead><tbody>{data.operations.map(operation => <tr key={operation.operation_id}>
          <td><strong>{t(operation.kind === 'search' ? 'connector.searchActivity' : 'connector.contactActivity')}</strong><small>{t('connector.people', { count: operation.delivered ?? operation.units })}</small></td>
          <td>{date(operation.created_at)}</td><td><span className={`settings-status ${operation.status === 'settled' ? 'success' : ''}`}>{t(operation.status === 'settled' ? 'connector.completed' : 'connector.inProgress')}</span></td>
          <td>{operation.status === 'settled' ? money(operation.charged_cents) : t('connector.held', { amount: money(operation.reserved_cents) })}</td>
        </tr>)}</tbody></table></div>}
        {data?.has_more && <p className="settings-footnote">{t('connector.latestOnly')}</p>}
      </section>
    </>}
  </div>;
}
